import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { isJSONDocNode } from '@atlaskit/editor-json-transformer/main';

import { canDeferPageRestrictions } from '@confluence/page-restrictions-context/entry-points/canDeferPageRestrictions.ts';
import { warmContentBodyAdfCache } from '@confluence/adf-analysis-core/entry-points/warmContentBodyAdfCache.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { preloadOutgoingLinks } from '@confluence/connectors/entry-points/preloadOutgoingLinks.ts';
import { preloadContent } from '@confluence/content-body/entry-points/preloadContent.ts';
import { preloadContentHeader } from '@confluence/content-header/entry-points/preloadContentHeader.ts';
import { preloadBylineAppContentProperties } from '@confluence/content-header/entry-points/preloadBylineAppContentProperties.ts';
import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites.ts';
import { preloadDraftRedirect } from '@confluence/content-prerequisites/entry-points/preloadDraftRedirect.ts';
import { preloadContentSmartLinks } from '@confluence/content-smartlinks/entry-points/preloadContentSmartLinks.ts';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadAutohighlights } from '@confluence/contextual-reading-aids/entry-points/preloadAutohighlights.ts';
import { getAutohighlightsQueryVariables } from '@confluence/contextual-reading-aids/entry-points/getAutohighlightsQueryVariables.ts';
import {
	preloadCustomHeaderAndFooter,
	primeCustomHeaderAndFooterFromRouteData,
} from '@confluence/custom-header-footer/entry-points/preloadCustomHeaderAndFooter.ts';
import { preloadMacrosSSR } from '@confluence/fabric-extension-handlers/entry-points/preloadMacrosSSR.ts';
import { preloadMacroData } from '@confluence/preload-macros/entry-points/preloadMacroData.ts';
import {
	preloadMediaToken,
	primeMediaTokenFromRouteData,
} from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import {
	getAttachmentDownloadDspAction,
	preloadIsAttachmentDownloadEnabled,
	primeIsAttachmentDownloadEnabledFromRouteData,
} from '@confluence/fabric-media-support/entry-points/preloadIsAttachmentDownloadEnabled.ts';
import { preloadFocusedInlineComment } from '@confluence/inline-comments/entry-points/preloadFocusedInlineComment.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { isRelayEnabled } from '@confluence/apollo-relay-interop/entry-points/isRelayEnabled.ts';
import { preloadAIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/preloadAIFloatingContextMenu.ts';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner.ts';
import {
	COMPANY_HUB_EDIT,
	EDIT_BLOG_V2,
	EDIT_PAGE_V2,
	HUB_EDIT,
	VIEW_BLOG,
	VIEW_BLOG_DATE_LEGACY,
	VIEW_PAGE,
} from '@confluence/named-routes/entry-points/routes.ts';
import { preloadEndOfPageRecommendation } from '@confluence/page-recommendations/entry-points/preloadEndOfPageRecommendation.ts';
import { preloadRecordLoomButton } from '@confluence/loom-utils/entry-points/preloadRecordLoomButton.ts';
import { preloadPresenceSettings } from '@confluence/team-presence/entry-points/preloadPresenceSettings.ts';
import {
	preloadViewPresenceBootstrap,
	trackViewPresencePreload,
} from '@confluence/team-presence/entry-points/preloadViewPresenceBootstrap.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { getAnalyticsBylineGateReason } from '@confluence/confluence-analytics/entry-points/analyticsBylineEligibility.ts';
import { preloadPageReactions } from '@confluence/reactions/entry-points/preloadPageReactions.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points/index.ts';
import { preloadPageStatusQuery } from '@confluence/query-preloaders-common/entry-points/preloadPageStatusQuery.ts';
import { preloadLegacyBridge } from '@confluence/query-preloaders-common/entry-points/preloadLegacyBridge.ts';
import {
	ATL_PAGE_CONTENT_FOOTER_ACTIONS,
	ATL_GENERAL,
	ATL_FOOTER,
	SYSTEM_CONTENT_BYLINE,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import {
	preloadWebPanelLocation,
	preloadWebPanelLocations,
} from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation.ts';
import { fg } from '@confluence/feature-gating';
import {
	expVal,
	expValEquals,
	UNSAFE_expValNoExposure,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { getPageExtensionListPreloadOptions } from '@confluence/forge-ui/entry-points/getPageExtensionListPreloadOptions.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { preloadRequestViewQuery } from '@confluence/no-permission/entry-points/preloadRequestViewQuery.ts';
import { preloadPendingRequestExists } from '@confluence/no-permission/entry-points/preloadPendingRequestExists.ts';
import { preloadIsSuperAdmin } from '@confluence/super-admin/entry-points/preloadIsSuperAdmin.ts';
import { preloadObjectComments } from '@confluence/object-comments/entry-points/preloadObjectComments.ts';
import { preloadOmnibar } from '@confluence/experiment-omnibar/entry-points/preloadOmnibar.ts';
import { preloadAddCommentPermissionCheck } from '@confluence/comments-panel-queries/entry-points/preloadAddCommentPermissionCheck.ts';
import { preloadLabels } from '@confluence/labels/entry-points/preloadLabels.tsx';
import { buildExternalLabelsAri } from '@confluence/labels/entry-points/useExternalLabels.ts';
import { preloadViewPageOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadViewPageOnboardingState.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import {
	getIsEmbeddedConfluence,
	getUsername,
	hasEmbeddedAllowedFeatures,
	shouldPreloadNavigationForTransition,
} from '@confluence/query-preloaders-common/entry-points/matchHelpers.ts';
import { matchBlogPage } from '@confluence/query-preloaders-common/entry-points/matchRoutes.ts';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime.ts';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery.ts';
import { CONFLUENCE_EDITION } from '@confluence/content-header/entry-points/defaultQueryVariables.js';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { preloadContentTitleWithByLine } from '@confluence/view-page-common/entry-points/preloadContentTitleWithByLine.ts';
import { preloadContentMode } from '@confluence/editor-content-mode-button/entry-points/preloadContentMode.ts';
import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { preloadSyncedBlocksData } from '@confluence/load-edit-page/entry-points/preloadSyncedBlocksData.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { preloadAudioContent } from '@confluence/audio/entry-points/preloadAudioContent.ts';
import { preloadApprovals } from '@confluence/approvals/entry-points/preloadApprovals.ts';
import { preloadIncomingLinksSsrState } from '@confluence/byline-connections/entry-points/preloadIncomingLinksSsrState.ts';
import { preloadOutboundBylineSsrState } from '@confluence/byline-connections/entry-points/preloadOutboundBylineSsrState.ts';
import { preloadRelatedContentSsrState } from '@confluence/byline-connections/entry-points/preloadRelatedContentSsrState.ts';
import { getSuperAdminClaim } from '@confluence/super-admin-claim';
import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';

import { preloadViewPagePersonalization } from './preloadViewPagePersonalization.ts';
import { readViewPageLoomPersonalization } from './readViewPageLoomPersonalization.ts';
import { createViewPageExtensionsAndRendererAssetsCoordinator } from './routeAssetsCoordinator.ts';

const DEFAULT_PRELOAD_RECOMMENDATION_TIMEOUT = 700;

const createPreloadContentTask = (
	contentId: string,
	isBlog: boolean,
	spaceKey: string,
	cloudId: string,
	activationId: string,
	isLoggedIn: boolean,
	isLicensed: boolean,
	isRovoButtonEnabled: boolean,
	contentNodesPromiseResolve?: (value: any) => void,
	userId?: string | null,
	agentSessionsEnabled?: boolean,
	isAccessLinkRoute?: boolean,
	shouldUseRouteOwnedMedia = false,
	shouldUseRouteOwnedDsp = false,
	shouldUseRouteOwnedCustomChrome = false,
	deferRestrictions = false,
	includeRestrictionAncestors = false,
	hasAnalyticsBylineGateReason = false,
): Promise<any> => {
	const autohighlightsQueryVariables =
		isLoggedIn &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_autohighlights_unified_query') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
			? getAutohighlightsQueryVariables({
					contentId,
					cloudId,
					activationId,
					contentType: isBlog ? 'blogpost' : 'page',
				})
			: undefined;

	// Managed (external) labels: warm the `useExternalLabels_query` spread on
	// `ContentUnifiedQuery` when the managed-labels feature is on.
	const externalLabelsQueryVariables =
		cloudId && contentId && fg('confluence_managed_external_labels_endpoint')
			? {
					cloudId,
					ari: buildExternalLabelsAri(cloudId, contentId, isBlog ? 'blogpost' : 'page'),
				}
			: undefined;
	const rovoActivationQueryVariables =
		!isBlog &&
		isLoggedIn &&
		isLicensed &&
		userId &&
		cloudId &&
		isRovoButtonEnabled &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		fg('confluence_rovo_agent_session_nudges')
			? { cloudId }
			: undefined;

	const dspAction = shouldUseRouteOwnedDsp ? getAttachmentDownloadDspAction() : undefined;

	return prepareGuardExceptionTask('Content', () =>
		preloadContent({
			contentId,
			isBlog,
			spaceKey,
			userId,
			...(isLoggedIn && cloudId && expValEquals('platform_editor_mention_rovo', 'isEnabled', true)
				? { cloudId }
				: {}),
			agentSessionsEnabled,
			// @ts-ignore -- Downstream packages are checked against the last generated package declaration.
			isAccessLinkRoute,
			// @ts-ignore -- Downstream packages are checked against the last generated package declaration.
			hasDocumentContractAnalyticsByline: true,
			// @ts-ignore -- Downstream packages are checked against the last generated package declaration.
			hasAnalyticsBylineGateReason,
			// @ts-ignore -- Downstream packages are checked against the last generated package declaration.
			hasDocumentContractContentHeader: true,
			...(autohighlightsQueryVariables ? { autohighlightsQueryVariables } : {}),
			// @ts-ignore -- Downstream packages are checked against the last generated content-body declaration.
			...(externalLabelsQueryVariables ? { externalLabelsQueryVariables } : {}),
			// @ts-ignore -- Downstream packages are checked against the last generated package declaration.
			includeRendererAssetsMedia: shouldUseRouteOwnedMedia,
			// @ts-ignore -- Downstream packages are checked against the last generated content-body declaration.
			...(dspAction ? { includeRendererAssetsDsp: true, rendererAssetsDspAction: dspAction } : {}),
			// @ts-ignore -- Downstream packages are checked against the last generated content-body declaration.
			...(shouldUseRouteOwnedCustomChrome ? { includeRendererAssetsCustomChrome: true } : {}),
			// @ts-ignore -- Downstream packages are checked against the last generated content-body declaration.
			...(rovoActivationQueryVariables ? { rovoActivationQueryVariables } : {}),
		}).then(
			async ({ result, hasErrors }): Promise<any> => {
				const contentNodes = result?.data?.content?.nodes;
				const boundaryParsedAdf = hasErrors
					? null
					: warmContentBodyAdfCache(contentNodes, (invalidContentId) => {
							ssrPrepareErrorsClient.submitError(
								new Error('Skipped shared ADF cache warm-up: invalid ADF document structure'),
								{
									contentId: invalidContentId,
									cloudId,
									isBlog,
									reason: 'invalid-adf-document-structure',
								},
							);
						});

				contentNodesPromiseResolve?.(contentNodes);
				const content = result?.data?.content?.nodes?.[0];
				if (
					deferRestrictions &&
					(hasErrors ||
						!content ||
						content.id !== contentId ||
						content.subType === 'live' ||
						content.subType === undefined ||
						(content.status !== 'current' && content.status !== 'archived'))
				) {
					await waitForAllReturnWhenError(
						preloadPageRestrictionsQuery(contentId, includeRestrictionAncestors),
					);
				}
				if (shouldUseRouteOwnedMedia) {
					if (hasErrors) {
						await preloadMediaToken(contentId, cloudId);
					} else {
						try {
							await primeMediaTokenFromRouteData(contentId, result?.data);
						} catch (error) {
							ssrPrepareErrorsClient.submitError(
								error instanceof Error
									? error
									: new Error('Failed to seed the media token cache from route data'),
								{
									contentId,
									cloudId,
									isBlog,
									reason: 'route-owned-media-cache-prime-failed',
								},
							);
							await preloadMediaToken(contentId, cloudId);
						}
					}
				}

				if (shouldUseRouteOwnedDsp && dspAction) {
					if (hasErrors) {
						await preloadIsAttachmentDownloadEnabled(contentId, spaceKey);
					} else {
						await primeIsAttachmentDownloadEnabledFromRouteData(
							contentId,
							spaceKey,
							dspAction,
							result?.data,
						);
					}
				}

				if (shouldUseRouteOwnedCustomChrome) {
					if (hasErrors) {
						await preloadCustomHeaderAndFooter(spaceKey);
					} else {
						primeCustomHeaderAndFooterFromRouteData(spaceKey, result?.data);
					}
				}

				// Report the content query's own errors as SSR prepare errors. `preloadContent` runs with
				// `errorPolicy: 'all'`, so a failing content query resolves rather than rejecting, and this
				// task resolves with its follow-up preloads instead of the query result — so nothing ever
				// inspected `result.errors`, and a page whose body failed to load reached the renderer with
				// nothing logged anywhere. Passing the settled result (rather than a task) is intentional:
				// `waitForAllReturnWhenError` reads the `{ hasErrors, errors }` shape that `query()`
				// resolves with, and skips the expected shapes (restricted page, not found) that `query()`
				// has already marked as handled — so only unexpected failures are reported.
				await waitForAllReturnWhenError(result);

				if (hasErrors) {
					const restrictedPageTasks: Promise<unknown>[] = [
						preloadContentPrerequisites(contentId, spaceKey),
						preloadRequestViewQuery(contentId),
						preloadDraftRedirect({
							contentId,
							draftShareId: window.location.search.match(/draftShareId=(.*?)(#|&|$)/)?.[1] ?? '',
						}),
					];

					// Preload the pending-access-request check so the restricted-content screen can
					// render the resolved title/message during SSR instead of blanking them
					if (
						userId &&
						cloudId &&
						UNSAFE_expValEqualsNoExposure(
							'cc_mohiti_restricted_content_screen_ab_exp',
							'isEnabled',
							true,
						)
					) {
						restrictedPageTasks.push(
							slowQueryGuard(
								'preloadPendingRequestExists',
								() => preloadPendingRequestExists(contentId, cloudId, userId),
								SLOW_QUERY_DEFAULT_TIMEOUT,
							),
						);
					}

					return waitForAllReturnWhenError(restrictedPageTasks);
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				} else if (process.env.REACT_SSR) {
					// No errors & no content means the page may be an unpublished draft
					if (result?.data?.content?.nodes?.length === 0) {
						return preloadDraftRedirect({
							contentId,
							draftShareId: window.location.search.match(/draftShareId=(.*?)(#|&|$)/)?.[1] ?? '',
						});
					}

					let parsedAdf: JSONDocNode | null = boundaryParsedAdf;

					if (!parsedAdf) {
						try {
							const adf = JSON.parse(result.data.content.nodes[0]?.body.dynamic.value);

							if (isJSONDocNode(adf)) {
								parsedAdf = adf;
							}
						} catch (error) {
							// JSON.parse error or unexpected data structure,
							// assume no synced blocks to avoid blocking page load

							ssrPrepareErrorsClient.submitError(
								new Error('Skipped synced blocks preload: failed to parse ADF document'),
								{
									contentId,
									cloudId,
									isBlog,
									errorMessage: error instanceof Error ? error.message : String(error),
								},
							);
						}
					}

					if (parsedAdf) {
						return waitForAllReturnWhenError([
							preloadSyncedBlocksData({
								contentId,
								cloudId: cloudId || '',
								isBlog,
								doc: parsedAdf,
								measureSegment: 'ssr-app/prepare/preloadQuery/renderer/syncedBlocksData',
							}),
							preloadMacrosSSR({
								contentId,
								cloudId,
								useMultipleMacrosQuery: false,
								contentNodes: result?.data?.content?.nodes,
							}),
						]);
					}

					return preloadMacrosSSR({
						contentId,
						useMultipleMacrosQuery: false,
						contentNodes: result?.data?.content?.nodes,
					});
				}
			},
			async (error: unknown) => {
				// A failed document request must not remove the old restrictions preload.
				contentNodesPromiseResolve?.(undefined);
				if (deferRestrictions) {
					await waitForAllReturnWhenError(
						preloadPageRestrictionsQuery(contentId, includeRestrictionAncestors),
					);
				}
				throw error;
			},
		),
	);
};

const createContentMainTasks = (params: {
	spaceKey: string;
	contentId: string;
	username?: string;
	edition: ConfluenceEdition | null;
	isPrevEditRoute: boolean;
	isBlog: boolean;
	cloudId?: string;
	activationId: string;
	isLoggedIn: boolean;
	isLicensed: boolean;
	isRovoButtonEnabled: boolean;
	contentNodesPromiseResolve?: (value: any) => void;
	contentNodesPromise: Promise<any>;
	isInitialLoad?: boolean;
	userId?: string | null;
	agentSessionsEnabled?: boolean;
	isAccessLinkRoute?: boolean;
	shouldUseRouteOwnedDsp: boolean;
	shouldUseRouteOwnedCustomChrome: boolean;
	deferRestrictions: boolean;
	hasAnalyticsBylineGateReason: boolean;
	routeAssetsCoordinator: ReturnType<typeof createViewPageExtensionsAndRendererAssetsCoordinator>;
}): Promise<unknown>[] => {
	const {
		spaceKey,
		contentId,
		username,
		edition,
		isPrevEditRoute,
		isBlog,
		cloudId,
		activationId,
		isLoggedIn,
		isLicensed,
		isRovoButtonEnabled,
		contentNodesPromiseResolve,
		contentNodesPromise,
		isInitialLoad,
		userId,
		agentSessionsEnabled,
		isAccessLinkRoute,
		shouldUseRouteOwnedDsp,
		shouldUseRouteOwnedCustomChrome,
		hasAnalyticsBylineGateReason,
		routeAssetsCoordinator,
	} = params;
	const shouldUseReadTimeFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_body_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	const shouldUseRouteOwnedPageMetadataBanner =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_metadata_banner_preload_dedup') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);

	const shouldUseRouteOwnedMedia =
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_media_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false) &&
		!expVal('confluence_media_token_preload_agg_query', 'isEnabled', false);
	return [
		preloadContentHeader({
			spaceKey,
			contentId,
			isBlog,
			username,
			edition,
			isPrevEditRoute,
			useNewContentTopper: true,
			isContentTypePublicLinkEligible: !isBlog,
			hasDocumentContractMetadata: true,
			hasDocumentContractClassification: true,
			hasDocumentContractBody: true,
			hasDocumentContractAnalyticsByline: true,
			hasDocumentContractContentHeader: true,
			// @ts-ignore -- Downstream packages are checked against the last generated content-body declaration.
			includeRendererAssetsMedia: shouldUseRouteOwnedMedia,
			// @ts-ignore -- Downstream packages are checked against the last generated package declaration.
			shouldPreloadPageMetadataBanner: !shouldUseRouteOwnedPageMetadataBanner,
			deferRestrictions: params.deferRestrictions,
		}),
		...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
			? []
			: [
					preloadContentTitleWithByLine(contentId, spaceKey),
					preloadPageTitleContentProperties({ contentId, spaceKey }),
				]),
		routeAssetsCoordinator.schedule('macro-ssr', () =>
			prepareGuardExceptionTask('Macros', () =>
				preloadMacrosSSR({
					contentId,
					useMultipleMacrosQuery: true,
					contentNodes: contentNodesPromise,
				}),
			),
		),
		routeAssetsCoordinator.schedule('macro-data', () =>
			prepareGuardExceptionTask('MacroData', () =>
				preloadMacroData({
					contentNodes: contentNodesPromise,
					context: { contentId, spaceKey },
					route: 'view',
				}),
			),
		),
		routeAssetsCoordinator.schedule('content', () =>
			createPreloadContentTask(
				contentId,
				isBlog,
				spaceKey,
				cloudId || '',
				activationId,
				isLoggedIn,
				isLicensed,
				isRovoButtonEnabled,
				contentNodesPromiseResolve,
				userId,
				agentSessionsEnabled,
				isAccessLinkRoute,
				shouldUseRouteOwnedMedia,
				shouldUseRouteOwnedDsp,
				shouldUseRouteOwnedCustomChrome,
				params.deferRestrictions,
				edition === CONFLUENCE_EDITION.FREE,
				hasAnalyticsBylineGateReason,
			),
		),
		...(!StatsigConfigurations.isKillSwitchOn(
			'confluence_frontend_view_page_relay_fragment_dedup',
		) && expVal('confluence_exp_vp_document', 'isEnabled', false)
			? []
			: [preloadPageStatusQuery(contentId)]),
		preloadLegacyBridge({ contentId, isBlog }),
		...(shouldUseReadTimeFragment
			? []
			: [preloadReadTime({ contentId, includeContentBody: false, useCache: true })]),
		// SmartLinks are only preloaded on initial load, never on transitions.
		...(isInitialLoad
			? [routeAssetsCoordinator.schedule('smart-links', () => preloadContentSmartLinks(contentId))]
			: (routeAssetsCoordinator.skip('smart-links'), [])),
	];
};

/*
 * preloadViewPageRoute preload queries are currently arranged in order of latency, with slowest
 * queries pushed to the tasks array first.
 */
export const preloadViewPageRoute = async (
	match: RouteMatch,
	url: string,
	isTransition: boolean = false,
	previousMatch: RouteMatch | null = null,
	/**
	 * Route-specific behaviour for callers that delegate here. `isAccessLinkRoute` is set by
	 * `preloadAccessLinkRoute`, which knows what route it is without having to inspect the URL.
	 */
	options: { isAccessLinkRoute?: boolean } = {},
): Promise<boolean | any[] | undefined> => {
	const {
		isLicensed,
		userId,
		edition,
		cloudId,
		accessStatus,
		isLoggedIn,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
		activationId,
	} = getPreloaderFnContext();
	const hasAnalyticsBylineGateReason = Boolean(
		getAnalyticsBylineGateReason({ edition, isLoggedIn, accessStatus, userId }),
	);
	const tasks: Promise<any>[] = [];
	// following functions will be triggered after we load data
	const postLoadingTasks: (() => Promise<any>)[] = [];

	const { spaceKey, contentId } = match.params;
	const username = getUsername(match);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const isBlog = Boolean(matchBlogPage(url));
	const isInitialLoad = !isTransition;
	const deferRestrictions =
		canDeferPageRestrictions(match) &&
		!options.isAccessLinkRoute &&
		!isEmbeddedConfluence &&
		!isMobileApp_DO_NOT_USE() &&
		!getSuperAdminClaim() &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_restrictions_on_demand') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	const shouldUseRouteOwnedMedia =
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_media_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false) &&
		!expVal('confluence_media_token_preload_agg_query', 'isEnabled', false);
	const shouldUseRouteOwnedDsp =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_dsp_reader') &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	const shouldUseRouteOwnedCustomChrome =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_custom_chrome_reader') &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	// Route preloaders cannot use useAIRovoButtonEnablement, so mirror its product-level
	// eligibility inputs to keep preload and hydrated query behavior aligned.
	const isRovoButtonEnabled = isAdminHubAIEnabled && isRovoLLMEnabled;
	const shouldUseRouteOwnedForgeExtensionList =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_forge_extension_list_reader') &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	const routeAssetsCoordinator = createViewPageExtensionsAndRendererAssetsCoordinator();
	// This guard schedules server-only preload work; it does not alter the React tree.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isReactSsr = Boolean(process.env.REACT_SSR);
	const shouldPreloadNavigation =
		!isEmbeddedConfluence && (isInitialLoad || shouldPreloadNavigationForTransition(previousMatch));
	const shouldPreloadBylineConnections =
		isReactSsr &&
		isInitialLoad &&
		isLoggedIn &&
		Boolean(cloudId) &&
		Boolean(contentId) &&
		// The existing byline UI owns exposure for this experiment.
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
		UNSAFE_expValEqualsNoExposure(
			'confluence_1p_and_3p_connection_byline_experiment',
			'isEnabled',
			true,
		);

	// Get isArchived from cached page data
	const { isArchived } = getCachedPageData(contentId);

	// Don't preload queries that will be updated by editor if previous route is edit_page
	const isPrevEditRoute =
		previousMatch?.name === EDIT_PAGE_V2.name ||
		previousMatch?.name === EDIT_BLOG_V2.name ||
		previousMatch?.name === COMPANY_HUB_EDIT.name ||
		previousMatch?.name === HUB_EDIT.name;

	if (isPrevEditRoute && isTransition) {
		// Preload for edit -> view transition
		return preloadContentHeader({
			spaceKey,
			contentId,
			isBlog,
			username,
			edition,
			isPrevEditRoute,
			useNewContentTopper: true,
			isContentTypePublicLinkEligible: !isBlog,
			hasDocumentContractMetadata: true,
			hasDocumentContractClassification: true,
			// @ts-ignore -- Downstream packages are checked against the last generated package declaration.
			hasDocumentContractAnalyticsByline: true,
			hasDocumentContractContentHeader: true,
			// @ts-ignore -- Downstream packages are checked against the last generated content-body declaration.
			includeRendererAssetsMedia: shouldUseRouteOwnedMedia,
		});
	}

	// ViewPageGateway wraps the entire page and renders nothing until useIsSuperAdmin settles, so
	// this has to resolve before render or no content is emitted. See preloadIsSuperAdmin for the
	// conditions under which it issues a request; it is a no-op for everyone else.
	tasks.push(preloadIsSuperAdmin());

	if (!isPrevEditRoute && !deferRestrictions) {
		tasks.push(preloadPageRestrictionsQuery(contentId, edition === CONFLUENCE_EDITION.FREE));
	}

	if (
		isLoggedIn &&
		edition === CONFLUENCE_EDITION.PREMIUM &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true)
	) {
		tasks.push(preloadApprovals({ cloudId, contentId, contentStatus: 'CURRENT', spaceKey }));
	}

	// Content types header (object header) + Unified Share and Restrict Button
	tasks.push(
		createDataFetcherForSuspense('content-types-header', () => {
			const contentTypesHeaderPreloadTasks: Promise<any>[] = [];
			if (
				StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') ||
				!expVal('confluence_exp_vp_document', 'isEnabled', false)
			) {
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				if (fg('confluence_frontend_usd_ssr')) {
					contentTypesHeaderPreloadTasks.push(preloadShareAndRestrictButtonQuery(contentId));
				}
			}
			if (
				StatsigConfigurations.isKillSwitchOn('confluence_vp_obj_header_frag_dedup') ||
				!expVal('confluence_exp_vp_document', 'isEnabled', false)
			) {
				contentTypesHeaderPreloadTasks.push(preloadContentTypesHeader(contentId));
			}

			// preload data for whether "unpublished changes" label should show in view page object header
			contentTypesHeaderPreloadTasks.push(
				preloadWebItemLocation({
					location: 'page.metadata.banner',
					contentId,
				}),
			);
			// (No need to call preloadPageRestrictionsQuery() here; it will be called by
			//  preloadContentHeader() )
			return contentTypesHeaderPreloadTasks;
		}),
	);

	if (
		isReactSsr &&
		isInitialLoad &&
		// Bifrost cannot deliver Statsig exposures. The byline render callsites evaluate the
		// same experiment through the Statsig client after SSR hydration.
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
		UNSAFE_expValNoExposure('confluence_ssr_byline', 'isEnabled', false)
	) {
		tasks.push(
			createDataFetcherForSuspense('byline-apps', () => [
				preloadWebItemLocation({
					contentId,
					location: SYSTEM_CONTENT_BYLINE,
				}),
				preloadUseExtensionList({
					contentId,
					spaceKey,
					moduleType: FORGE_MODULE_BYLINE,
				}).then(
					// Property keys depend on Forge discovery, not the rest of content-main.
					() => preloadBylineAppContentProperties(contentId),
				),
			]),
		);
	}

	// Content main
	let contentNodesPromiseResolve: ((value: any) => void) | undefined = undefined;
	const contentNodesPromise = new Promise<any>((resolve) => {
		contentNodesPromiseResolve = resolve;
	});
	tasks.push(
		createDataFetcherForSuspense('content-main', () => {
			const mainTasks = createContentMainTasks({
				spaceKey,
				contentId,
				username,
				edition,
				isPrevEditRoute,
				isBlog,
				cloudId,
				activationId,
				isLoggedIn,
				isLicensed,
				isRovoButtonEnabled,
				contentNodesPromiseResolve,
				contentNodesPromise,
				isInitialLoad,
				userId,
				// confluence-feature-gating/inline-usage: must be inlined
				agentSessionsEnabled: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
				isAccessLinkRoute: options.isAccessLinkRoute,
				shouldUseRouteOwnedDsp,
				shouldUseRouteOwnedCustomChrome,
				deferRestrictions,
				hasAnalyticsBylineGateReason,
				routeAssetsCoordinator,
			});

			if (shouldPreloadBylineConnections) {
				mainTasks.push(
					preloadIncomingLinksSsrState({
						cloudId,
						contentId,
						contentType: isBlog ? 'blogpost' : 'page',
						route: 'view',
					}),
				);
				mainTasks.push(preloadOutboundBylineSsrState(contentId));
				mainTasks.push(
					preloadRelatedContentSsrState({
						cloudId,
						activationId,
						contentId,
						contentType: isBlog ? 'blogpost' : 'page',
						route: 'view',
					}),
				);
			}

			if (isInitialLoad && isLoggedIn) {
				const preloadRecommendations = () =>
					preloadEndOfPageRecommendation({
						contentId,
						spaceKey,
						contentType: isBlog ? 'blogpost' : 'page',
						cloudId,
					});
				const createEndOfPageDataFetcher = (includeRecommendations: boolean) =>
					createDataFetcherForSuspense('end-of-page-recommendations', () => {
						const endOfPageTasks: Promise<any>[] = includeRecommendations
							? [preloadRecommendations()]
							: [];
						if (
							!isBlog &&
							// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
							UNSAFE_expValEqualsNoExposure(
								'confluence_3p_in_related_content',
								'shouldEvaluate',
								true,
							)
						) {
							endOfPageTasks.push(preloadOutgoingLinks(contentId));
						}
						return endOfPageTasks;
					});

				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				if (fg('confluence_eop_recs_content_main_preload')) {
					// Layout and comment consumers share this query with the end-of-page widget,
					// so the common content-main ancestor owns it in the treatment path.
					mainTasks.push(preloadRecommendations(), createEndOfPageDataFetcher(false));
				} else {
					mainTasks.push(createEndOfPageDataFetcher(true));
				}
			}

			return mainTasks;
		}),
	);

	if (
		StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') ||
		!expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		tasks.push(preloadContentMode({ contentId, isDraft: false, useCache: true, isArchived }));
	}

	// Navigation, Page tree and blog tree for blog post
	if (shouldPreloadNavigation) {
		tasks.push(
			preloadNavigationTasks(
				spaceKey,
				contentId,
				isLicensed,
				isBlog,
				true,
				previousMatch?.params?.spaceKey,
			),
		);
	}
	// Space details
	tasks.push(preloadSpaceDetail(spaceKey));

	// Inline Comments
	if (
		match?.query.focusedCommentId &&
		!(
			isRelayEnabled('@confluence/object-comments', {
				operationName: 'ObjectCommentContainerPreloadObjectCommentsQuery',
			}) &&
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_query') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false)
		)
	) {
		tasks.push(preloadFocusedInlineComment(match?.query.focusedCommentId as string, contentId));
	}

	// Reactions
	if (!isEmbeddedConfluence || hasEmbeddedAllowedFeatures('page-reactions', match)) {
		// Preload whether the server has enabled reactions at all to show placeholder
		tasks.push(preloadWebPanelLocation({ contentId, location: ATL_PAGE_CONTENT_FOOTER_ACTIONS }));
		// Preload reactions asynchronously so SSR won't wait for it
		// it's best effort preloading so if it preloads then we have the data, otherwise SPA can load it
		if (
			StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') ||
			!expVal('confluence_exp_vp_document', 'isEnabled', false)
		) {
			tasks.push(
				preloadPageReactions({
					pageId: contentId,
					containerId: spaceKey,
				}),
			);
		}
	}

	// Recommendations
	// On initial load these are preloaded inside a suspense data fetcher above, so only transitions
	// need this standalone preload. Recommendations are only
	// preloaded for authenticated users (knowledge_discovery service requires auth).
	if (!isInitialLoad && isLoggedIn) {
		const recommendationsTimeout = StatsigConfigurations.getNumber(
			'confluence_frontend_preload_recommendation_timeout',
			DEFAULT_PRELOAD_RECOMMENDATION_TIMEOUT,
		);
		tasks.push(
			slowQueryGuard(
				'preloadEndOfPageRecommendation',
				() => {
					return preloadEndOfPageRecommendation({
						contentId,
						spaceKey,
						contentType: isBlog ? 'blogpost' : 'page',
						cloudId,
					});
				},
				recommendationsTimeout,
			),
		);
	}

	// Stuff only needed for initial load
	if (isInitialLoad) {
		if (!shouldUseRouteOwnedMedia) {
			tasks.push(
				// We only refresh token when it is about to expire.
				preloadMediaToken(contentId, cloudId || ''),
			);
		}
		if (!shouldUseRouteOwnedCustomChrome) {
			tasks.push(preloadCustomHeaderAndFooter(spaceKey));
		}
		if (!shouldUseRouteOwnedDsp) {
			tasks.push(preloadIsAttachmentDownloadEnabled(contentId, spaceKey));
		}
	}

	// Labels
	if (
		isTransition &&
		(StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') ||
			!expVal('confluence_exp_vp_document', 'isEnabled', false))
	) {
		tasks.push(preloadLabels({ contentId }));
	}

	// Managed (external) labels are warmed via the `useExternalLabels_query`
	// fragment on `ContentUnifiedQuery` (see `externalLabelsQueryVariables` above),
	// so a dedicated preload task is no longer needed. The legacy label picker's
	// standalone `useExternalLabels` read is `store-or-network`, so it reads that
	// warmed store or falls back to network on dialog open.

	// Preload web panel atl_general
	if (expValEquals('web_panel_query_batch_call', 'isEnabled', true)) {
		tasks.push(
			preloadWebPanelLocations({
				contentId,
				locations: [ATL_GENERAL, ATL_FOOTER],
			}),
		);
	} else {
		tasks.push(
			preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
			preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
		);
	}

	// Object Sidebar Control + AI Button
	if (isAdminHubAIEnabled) {
		tasks.push(
			createDataFetcherForSuspense('ai-floating-context-menu', () =>
				preloadAIFloatingContextMenu(contentId, isBlog),
			),
		);
	}

	// Preload Audio Listen Button
	if (
		StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') ||
		!expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		tasks.push(preloadAudioContent(contentId));
	}

	const extensionListOptions = getPageExtensionListPreloadOptions({
		contentId,
		spaceKey,
		isTransition,
		preloadMacros:
			!shouldUseRouteOwnedForgeExtensionList ||
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('conf_frontend_forge_static_macro_mvp'),
	});
	if (extensionListOptions) {
		const extensionListLoader = () => preloadUseExtensionList(extensionListOptions);
		tasks.push(
			shouldUseRouteOwnedForgeExtensionList
				? routeAssetsCoordinator.schedule('forge-extension-list', extensionListLoader)
				: extensionListLoader(),
		);
	} else if (shouldUseRouteOwnedForgeExtensionList) {
		routeAssetsCoordinator.skip('forge-extension-list');
	}

	// Preload Loom button in Action Bar
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		isLoggedIn
	) {
		tasks.push(
			preloadRecordLoomButton({
				isViewPage: true,
				personalizationInputs: preloadViewPagePersonalization({
					cloudId,
					activationId,
					contentId,
					spaceKey,
					isLoggedIn,
					accessStatus,
					isBlog,
					isAdminHubAIEnabled,
					// Activate other groups with their readers in CCP-9560, preserving loading boundaries.
					groups: { loom: true },
				}).then(readViewPageLoomPersonalization),
				cloudId,
				activationId,
				accessStatus,
				isLoggedIn,
				userId,
			}),
		);
	}

	// Only ordinary page/blog views use the combined contract. Wait for content metadata
	// so live pages (and missing/mismatched content) retain the legacy preload.
	if (
		!options.isAccessLinkRoute &&
		!isEmbeddedConfluence &&
		!isMobileApp_DO_NOT_USE() &&
		(match.name === VIEW_PAGE.name ||
			match.name === VIEW_BLOG.name ||
			match.name === VIEW_BLOG_DATE_LEGACY.name) &&
		// The bootstrap/cache handoff is native Relay; preserve the legacy flow when
		// either query routes to Apollo (including SSR before rollout config is ready).
		isRelayEnabled('@confluence/team-presence', {
			operationName: 'viewPresenceBootstrapQuery',
		}) &&
		isRelayEnabled('@confluence/team-presence', {
			operationName: 'ContentHeaderPresenceContentSettingsQuery',
		}) &&
		expVal('confluence_exp_view_presence_contract', 'isEnabled', false)
	) {
		const presenceVariables = { cloudId, contentId, spaceKey };
		tasks.push(
			trackViewPresencePreload(presenceVariables, async () => {
				const nodes = await contentNodesPromise;
				const content = nodes?.[0];
				return content?.id === contentId &&
					content.subType !== undefined &&
					content.subType !== 'live'
					? preloadViewPresenceBootstrap(presenceVariables)
					: preloadPresenceSettings({ cloudId, spaceKey });
			}),
		);
	} else {
		tasks.push(preloadPresenceSettings({ cloudId, spaceKey }));
	}

	// Preload definition autohighlights
	// Only preload autohighlights for authenticated users (knowledge_discovery service requires auth)
	if (
		isLoggedIn &&
		(StatsigConfigurations.isKillSwitchOn('confluence_vp_autohighlights_unified_query') ||
			!expVal('confluence_exp_vp_document', 'isEnabled', false))
	) {
		tasks.push(
			preloadAutohighlights({
				contentId,
				cloudId,
				activationId,
				contentType: isBlog ? 'blogpost' : 'page',
			}),
		);
	}

	tasks.push(
		createDataFetcherForSuspense('object-comments', () => [
			// Preload footer comments. SSRing live pages necessitates non-server (i.e. transition)
			// preloading, so enable here too for consistency in the footer component.
			preloadObjectComments(contentId, match?.query.focusedCommentId as string | undefined),
			// The conversation query owns this permission data when its fragment reader is enabled.
			...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_data_frag_reader') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false)
				? []
				: [preloadAddCommentPermissionCheck(contentId, ['draft', 'current'])]),
		]),
	);

	// Preload onboarding state for view page
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS) &&
		expVal('confluence_pgxp_p2m_batch_1', 'isEnabled', false)
	) {
		tasks.push(preloadViewPageOnboardingState());
	}

	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (fg('cc-ai-growth-preload-omnibar')) {
		tasks.push(preloadOmnibar());
	}

	// The document-owned header supplies the banner fragment in treatment.
	if (
		isEmbeddedConfluence ||
		Boolean(match.query.versionOverride || match.query.lite || match.query.draftShareId) ||
		StatsigConfigurations.isKillSwitchOn('confluence_vp_header_frag_reader') ||
		StatsigConfigurations.isKillSwitchOn('confluence_vp_smart_create_banner_frag_reader') ||
		!expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		tasks.push(preloadSmartCreateFeedbackBanner(contentId));
	} else {
		tasks.push(
			contentNodesPromise.then((nodes) => {
				const content = nodes?.[0];
				// Live documents and missing/mismatched owner data still need the legacy preload.
				if (
					content?.id !== contentId ||
					content?.subType === 'live' ||
					!content?.contentHeaderFragment
				) {
					return preloadSmartCreateFeedbackBanner(contentId);
				}
				return undefined;
			}),
		);
	}

	const errors = await waitForAllReturnWhenError(tasks);
	const postLoadingErrors = await waitForAllReturnWhenError(postLoadingTasks.map((task) => task()));

	if (errors || postLoadingErrors) {
		return [...(errors || []), ...(postLoadingErrors || [])];
	}
};
